import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import store, { templatesAction } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { formatDate, handleAdminError, handleAdminInfo, handleAdminSuccess } from '../../common/SharedFunctions';
import { RefreshEvents } from '../../common/SharedEvents';
import { TemplateType, TokenCategoryTypeOptions } from '@apply/constants';
import { TemplatesApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { NumericFormat } from 'react-number-format';
import Tabs from 'devextreme-react/tabs';
import { LoadPanel } from 'devextreme-react/load-panel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { HtmlEditor } from './HtmlEditor';
import pretty from 'pretty';

const EditTemplateModal = () => {
    const { isOpen, templateId, templateTypeId = TemplateType.ICCA } = useSelector((state) => state.templates.editTemplateModal);
    const { dispatch } = store;
    const templatesApi = new TemplatesApi.TemplatesApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const styles = {
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
            marginTop: '5px',
        },
        buttonContainer2: {
            textAlign: 'right',
            padding: '5px',
        },
        sectionHeader: { margin: '10px 0' },
        formColumn: { paddingBottom: '5px', paddingRight: '5px' },
        tokenCodeColumn: {
            width: '45%',
        },
        tokenHeaderFlexContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        tokenHeaderIcon: {
            fontSize: '1.5rem',
            fontWeight: '100',
            color: '#2196f3',
        },
        property_box: {
            border: '1px solid silver',
            borderRadius: '0 0 5px 5px',
            padding: '10px',
        },
    };

    const [tokens, set_tokens] = useState([]);
    const [tokenCategory, set_tokenCategory] = useState(1);
    const [tokenCategories, set_tokenCategories] = useState([]);

    const [versionList, set_versionList] = useState([]);
    const [version, set_version] = useState(null);
    const [templateName, set_templateName] = useState('');
    const [contentHtml, set_contentHtml] = useState('');

    //ICCA Specific
    const [initialLoanRate, set_initialLoanRate] = useState('');
    const [additionalLoanRate, set_additionalLoanRate] = useState('');
    const [daysToReferralDeadline, set_daysToReferralDeadline] = useState('');
    const [daysToContractExpiration, set_daysToContractExpiration] = useState('');

    //Notice Specific
    const [emailSubject, set_emailSubject] = useState('');
    const [emailBodyPlain, set_emailBodyPlain] = useState('');
    const [emailBodyHtml, set_emailBodyHtml] = useState('');

    const [tabs, set_tabs] = useState([{ text: 'Content' }, { text: 'Email' }]);
    const [tabIndex, set_tabIndex] = useState(0);

    const [tabs2] = useState([{ text: 'HTML' }, { text: 'Plain Text' }]);
    const [tabIndex2, set_tabIndex2] = useState(0);

    const [isSaving, set_isSaving] = useState(false);

    const handleClose = () =>
        dispatch(
            templatesAction.setEditTemplateModal({
                editTemplateModal: { isOpen: false },
            })
        );

    useEffect(() => {
        async function templateTypeIdChanged() {
            if (!templateTypeId) {
                return;
            }
            await loadTokens(templateTypeId);
            await loadTokens(templateTypeId);
        }

        templateTypeIdChanged();
    }, [templateTypeId]);

    useEffect(() => {
        async function templateIdChanged() {
            if (!templateId) {
                resetState();
                return;
            }
            await loadTemplate();
        }

        templateIdChanged();
    }, [templateId]);

    const resetState = () => {
        set_isSaving(false);
        set_versionList([]);
        set_version({
            templateTypeId: templateTypeId,
        });
        set_tabIndex(0);
        set_tabIndex2(0);
        set_templateName('');
        set_emailSubject('');
        set_emailBodyHtml('');
        set_emailBodyPlain('');
        set_contentHtml('');
    };

    const loadTokens = async (templateTypeId) => {
        const data = await templatesApi.getTokens(templateTypeId);
        const catgs = getTokenCategories(data);
        set_tokens(data);
        set_tokenCategories(catgs);
    };

    const getTokenCategories = (tokens) => {
        if (!tokens) {
            return [];
        }
        //Get unique category Ids
        const catgIds = [...new Set(tokens.map((item) => item.category))];
        const catgs = [];
        catgIds.forEach((id) => {
            var catg = TokenCategoryTypeOptions.find((c) => c.id === id);
            if (catg) {
                catgs.push(catg);
            }
        });
        return catgs;
    };

    const loadTemplate = async () => {
        resetState();
        if (!templateId) {
            return;
        }

        const response = await templatesApi.getTemplateVersions(templateId);
        if (response.statusCode !== 200) {
            throw new Error('Could not load the Template Version.');
        }

        const versions = response.result;
        set_versionList(versions);

        if (versions.length === 0) {
            return;
        }

        const ver = versions[0];
        set_version(ver);
        set_contentHtml(pretty(ver.content));

        switch (ver.templateTypeId) {
            case TemplateType.Notices:
                set_emailSubject(ver.emailSubject);
                set_emailBodyHtml(pretty(ver.emailBodyHtml));
                set_emailBodyPlain(ver.emailBodyPlain);
                if (ver.generatesPdf) {
                    set_tabs([
                        { text: 'Content', disabled: false },
                        { text: 'Email', disabled: false },
                    ]);
                    set_tabIndex(0);
                } else {
                    //set_content('');
                    set_contentHtml('');
                    set_tabs([
                        { text: 'Content', disabled: true },
                        { text: 'Email', disabled: false },
                    ]);
                    set_tabIndex(1);
                }
                set_tabIndex2(0);
                break;
            case TemplateType.ICCA:
                set_initialLoanRate(ver.initialLoanRate);
                set_additionalLoanRate(ver.additionalLoanRate);
                set_daysToReferralDeadline(ver.daysToReferralDeadline);
                set_daysToContractExpiration(ver.daysToContractExpiration);
                break;
        }
        await loadTokens(ver.templateTypeId);
    };

    const onVersionChange = (event) => {
        if (!versionList) {
            return;
        }

        const version = versionList.find((version) => version.versionId === parseInt(event.target.value));
        set_version(version);
    };

    const validateRequiredFields = () => {
        let isOk = true;
        isOk = !version.generatesPdf || !!contentHtml;
        if (!isOk) {
            set_tabIndex(0);
            handleAdminError(new Error('Missing Template Content'), 'Missing Template Content!');
            return false;
        }
        if (version.templateTypeId === TemplateType.ICCA) {
            isOk = isOk && !!initialLoanRate && !!additionalLoanRate && !!daysToReferralDeadline && !!daysToContractExpiration;
            if (!isOk) {
                handleAdminError(new Error('Missing ICCA Fields'), 'Missing ICCA Fields!');
                return false;
            }
        }
        if (version.templateTypeId === TemplateType.Notices) {
            isOk = isOk && !!emailSubject;
            if (!isOk) {
                set_tabIndex(1);
                handleAdminError(new Error('Missing Email Subject'), 'Missing Email Subject!');
                return false;
            }
            isOk = isOk && !!emailBodyHtml;
            if (!isOk) {
                set_tabIndex(1);
                set_tabIndex2(0);
                handleAdminError(new Error('Missing HTML Email Body'), 'Missing HTML Email Body!');
                return false;
            }
            isOk = isOk && !!emailBodyPlain;
            if (!isOk) {
                set_tabIndex(1);
                set_tabIndex2(1);
                handleAdminError(new Error('Missing Plain Text Email Body'), 'Missing Plain Text Email Body!');
                return false;
            }
        }
        return true;
    };

    const onSave = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isOk = validateRequiredFields();
        if (!isOk) {
            return;
        }

        try {
            set_isSaving(true);

            handleAdminInfo('Saving new version...');

            const model = {
                templateId: version?.templateId ?? 0,
                templateTypeId: version.templateTypeId,
                templateName,
                content: contentHtml,
            };

            if (version.templateTypeId === TemplateType.ICCA) {
                model.initialLoanRate = parseFloat(initialLoanRate);
                model.additionalLoanRate = parseFloat(additionalLoanRate);
                model.daysToReferralDeadline = parseInt(daysToReferralDeadline);
                model.daysToContractExpiration = parseInt(daysToContractExpiration);
            }

            if (version.templateTypeId === TemplateType.Notices) {
                model.emailSubject = emailSubject;
                model.emailBodyHtml = emailBodyHtml;
                model.emailBodyPlain = emailBodyPlain;
            }
            await templatesApi.saveTemplateVersion(model);

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('New Template Version saved!');

            setTimeout(handleClose, 3000);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred! Please try again.');
        } finally {
            set_isSaving(false);
        }
    };

    const onTabsSelectionChanged = (args) => {
        if (args.name === 'selectedIndex') {
            set_tabIndex(args.value);
        }
    };

    const onTabsSelectionChanged2 = (args) => {
        if (args.name === 'selectedIndex') {
            set_tabIndex2(args.value);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            maxWidth='lg'
            open={isOpen}
            onClose={handleClose}
            aria-labelledby='form-dialog-title'
        >
            <div className='modal-container'>
                <div className='modal-backdrop'></div>
                <div
                    className='modal'
                    tabIndex='-1'
                    role='dialog'
                    style={{ width: '1300px' }}
                >
                    <div
                        className='modal-dialog'
                        role='document'
                    >
                        <div
                            className='page-header'
                            role='banner'
                        >
                            <h1 style={{ height: '50px', marginTop: '15px' }}>{templateId ? 'Edit Template' : 'Add Template'}</h1>
                            <button
                                type='button'
                                className='close'
                                data-dismiss='modal'
                                aria-label='Close'
                                onClick={handleClose}
                            >
                                <span aria-hidden='true'>&times;</span>
                            </button>
                        </div>
                        <div style={{ padding: '0', margin: '10px' }}>
                            <form
                                name='editBorrowerForm'
                                onSubmit={(event) => onSave(event)}
                                className={'standard-form'}
                            >
                                <div className='scrollable-content'>
                                    {version?.templateId && <h2>{version?.templateName}</h2>}
                                    <div className='card summary-block'>
                                        <div
                                            className='card-section'
                                            style={{ paddingBottom: '0' }}
                                        >
                                            <div className='row'>
                                                {version?.templateId ? (
                                                    <div
                                                        className='columns small-6 large-6 end'
                                                        style={{
                                                            paddingBottom: '0',
                                                        }}
                                                    >
                                                        <label htmlFor='referralDeadline'>Available Versions</label>
                                                        <select
                                                            value={version?.versionId}
                                                            onChange={(event) => onVersionChange(event)}
                                                        >
                                                            {versionList &&
                                                                versionList.map((version) => {
                                                                    return (
                                                                        <option
                                                                            key={version.versionId}
                                                                            value={version.versionId}
                                                                        >
                                                                            {version.versionName}
                                                                        </option>
                                                                    );
                                                                })}
                                                        </select>
                                                        {version && (
                                                            <p className='note'>
                                                                Edited: {version.lastUpdatedBy} on{' '}
                                                                {formatDate(version.lastUpdateAt)}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div
                                                        className='columns small-6 large-6 end'
                                                        style={{
                                                            paddingBottom: '0',
                                                        }}
                                                    >
                                                        <label htmlFor='templateName'>
                                                            Template Name
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <input
                                                            id='templateName'
                                                            type='text'
                                                            maxLength={100}
                                                            value={templateName}
                                                            onChange={(event) => set_templateName(event.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            {version?.templateTypeId === TemplateType.ICCA && (
                                                <div className='row'>
                                                    <div className='columns small-12 medium-6 large-3'>
                                                        <label htmlFor='referralDeadline'>
                                                            Days until Referral Deadline <span className='req'>*</span>
                                                        </label>
                                                        <NumericFormat
                                                            thousandSeparator=','
                                                            id='referralDeadline'
                                                            name='referralDeadline'
                                                            value={daysToReferralDeadline}
                                                            decimalScale={0}
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                set_daysToReferralDeadline(values.floatValue);
                                                            }}
                                                            required={true}
                                                            isAllowed={(values, sourceInfo) => {
                                                                const value = values?.floatValue;
                                                                return value === undefined || value === null || value > 0;
                                                            }}
                                                        />
                                                    </div>
                                                    <div className='columns small-12  medium-6  large-3'>
                                                        <label htmlFor='contractExpiration'>
                                                            Days until Contract Expiration <span className='req'>*</span>
                                                        </label>
                                                        <NumericFormat
                                                            thousandSeparator=','
                                                            id='contractExpiration'
                                                            name='contractExpiration'
                                                            value={daysToContractExpiration}
                                                            decimalScale={0}
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                set_daysToContractExpiration(values.floatValue);
                                                            }}
                                                            required={true}
                                                            isAllowed={(values, sourceInfo) => {
                                                                const value = values?.floatValue;
                                                                return value === undefined || value === null || value > 0;
                                                            }}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 medium-6 large-3'>
                                                        <label htmlFor='initialRate'>
                                                            Initial Loan Rate <span className='req'>*</span>
                                                        </label>
                                                        <NumericFormat
                                                            thousandSeparator=','
                                                            id='initialRate'
                                                            name='initialRate'
                                                            value={initialLoanRate}
                                                            decimalScale={2}
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                set_initialLoanRate(values.floatValue);
                                                            }}
                                                            required={true}
                                                            isAllowed={(values, sourceInfo) => {
                                                                const value = values?.floatValue;
                                                                return value === undefined || value === null || value > 0;
                                                            }}
                                                        />
                                                    </div>
                                                    <div className='columns small-12 medium-6 large-3'>
                                                        <label htmlFor='additionalRate'>
                                                            Additional Loan Rate <span className='req'>*</span>
                                                        </label>
                                                        <NumericFormat
                                                            thousandSeparator=','
                                                            id='additionalRate'
                                                            name='additionalRate'
                                                            value={additionalLoanRate}
                                                            decimalScale={3}
                                                            allowNegative={false}
                                                            onValueChange={(values) => {
                                                                set_additionalLoanRate(values.floatValue);
                                                            }}
                                                            required={true}
                                                            isAllowed={(values, sourceInfo) => {
                                                                const value = values?.floatValue;
                                                                return value === undefined || value === null || value > 0;
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {version?.templateTypeId === TemplateType.Notices && (
                                        <Tabs
                                            showNavButtons={true}
                                            width={'300px'}
                                            dataSource={tabs}
                                            selectedIndex={tabIndex}
                                            onOptionChanged={onTabsSelectionChanged}
                                        />
                                    )}
                                    <div style={styles.property_box}>
                                        {tabIndex === 1 && (
                                            <div className='row'>
                                                <div
                                                    className='columns small-12 large-12 end'
                                                    style={{
                                                        paddingLeft: '0',
                                                        paddingRight: '0',
                                                    }}
                                                >
                                                    <label htmlFor='emailSubject'>
                                                        Email Subject <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='emailSubject'
                                                        type='text'
                                                        maxLength={200}
                                                        value={emailSubject}
                                                        onChange={(e) => set_emailSubject(e.target.value)}
                                                        required={true}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className='text-editor-container has-sidebar'>
                                            <div
                                                className='text-editor'
                                                style={{ maxWidth: '800px' }}
                                            >
                                                {tabIndex === 0 && (
                                                    <HtmlEditor
                                                        templateId={templateId}
                                                        value={contentHtml}
                                                        onChanged={set_contentHtml}
                                                    />
                                                )}
                                                {tabIndex === 1 && (
                                                    <>
                                                        <label htmlFor='emailBody'>
                                                            Email Body <span className='req'>*</span>
                                                        </label>
                                                        <Tabs
                                                            showNavButtons={true}
                                                            width={'300px'}
                                                            dataSource={tabs2}
                                                            selectedIndex={tabIndex2}
                                                            onOptionChanged={onTabsSelectionChanged2}
                                                        />
                                                        <div style={styles.property_box}>
                                                            {tabIndex2 === 0 && (
                                                                <HtmlEditor
                                                                    templateId={templateId}
                                                                    value={emailBodyHtml}
                                                                    onChanged={set_emailBodyHtml}
                                                                />
                                                            )}
                                                            {tabIndex2 === 1 && (
                                                                <textarea
                                                                    rows={20}
                                                                    id='emailBodyPlain'
                                                                    type='text'
                                                                    value={emailBodyPlain}
                                                                    onChange={(e) => set_emailBodyPlain(e.target.value)}
                                                                    required={true}
                                                                />
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            {tokenCategories && tokenCategories.length > 0 && (
                                                <div
                                                    className='text-editor-sidebar'
                                                    style={{
                                                        paddingLeft: '10px',
                                                    }}
                                                >
                                                    <div className='sidebar-box'>
                                                        <div
                                                            style={styles.tokenHeaderFlexContainer}
                                                            className='sidebar-select standard-form'
                                                        >
                                                            <select
                                                                style={{
                                                                    width: '90%',
                                                                }}
                                                                value={tokenCategory}
                                                                onChange={(event) =>
                                                                    set_tokenCategory(parseInt(event.target.value))
                                                                }
                                                            >
                                                                {tokenCategories.map((category) => {
                                                                    return (
                                                                        <option
                                                                            key={category.id}
                                                                            value={category.id}
                                                                        >
                                                                            {category.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                            <span className='iccaTokenSummary'>
                                                                <FontAwesomeIcon
                                                                    icon={faQuestionCircle}
                                                                    style={styles.tokenHeaderIcon}
                                                                />
                                                                <span className='text'>
                                                                    Tokens can be used in the agreement customize the text. The{' '}
                                                                    {'{{token}}'} will be replaced by the corresponding value by
                                                                    the system when the report is generated.
                                                                    <br />
                                                                    <br />
                                                                    You can find a list of tokens and what they represent below:
                                                                </span>
                                                            </span>
                                                        </div>
                                                        <div className='sidebar-scollable'>
                                                            <table className='editor-codes'>
                                                                <thead>
                                                                    <tr>
                                                                        <th style={styles.tokenCodeColumn}>Code</th>
                                                                        <th>Desciption</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {tokens &&
                                                                        tokens
                                                                            .filter((token) => token.category === tokenCategory)
                                                                            .map((token, index) => {
                                                                                if (token.noticeTypes) {
                                                                                    if (
                                                                                        token.noticeTypes.includes(
                                                                                            version.noticeTypeId
                                                                                        )
                                                                                    ) {
                                                                                        return (
                                                                                            <tr key={'token' + index}>
                                                                                                <td
                                                                                                    style={styles.tokenCodeColumn}
                                                                                                >{`{{${token.code}}}`}</td>
                                                                                                <td>{token.description}</td>
                                                                                            </tr>
                                                                                        );
                                                                                    } else {
                                                                                        return null;
                                                                                    }
                                                                                } else {
                                                                                    return (
                                                                                        <tr key={'token' + index}>
                                                                                            <td
                                                                                                style={styles.tokenCodeColumn}
                                                                                            >{`{{${token.code}}}`}</td>
                                                                                            <td>{token.description}</td>
                                                                                        </tr>
                                                                                    );
                                                                                }
                                                                            })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='submit'
                                        className='button primary'
                                        title='Save changes and close'
                                    >
                                        Save & Create New Version
                                    </button>
                                    &nbsp;
                                    <button
                                        type='button'
                                        className='button hollow'
                                        data-dismiss='modal'
                                        onClick={handleClose}
                                        title='Abandon changes and close'
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <LoadPanel
                                    hideOnOutsideClick={false}
                                    visible={isSaving}
                                    message='Saving...'
                                />
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default EditTemplateModal;
